/**
 * Resource library types
 */

// Enums
export enum FolderType {
  COPYWRITING = 'copywriting',
  MATERIAL = 'material',
}

export enum ResourceScope {
  ENTERPRISE = 'enterprise',
  PERSONAL = 'personal',
}

export enum MaterialType {
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOCUMENT = 'document',
  OTHER = 'other',
}

// Resource Folder
export interface ResourceFolder {
  id: number;
  name: string;
  parent_id: number | null;
  folder_type: FolderType;
  scope: ResourceScope;
  owner_id: number | null;
  sort_order: number;
  visible_to_all: boolean;
  visible_roles: string[] | null;
  resource_count?: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  children?: ResourceFolder[];
}

export interface ResourceFolderCreate {
  name: string;
  parent_id?: number | null;
  folder_type: FolderType;
  scope: ResourceScope;
  sort_order?: number;
  visible_to_all?: boolean;
  visible_roles?: string[];
}

export interface ResourceFolderUpdate {
  name?: string;
  parent_id?: number | null;
  sort_order?: number;
  visible_to_all?: boolean;
  visible_roles?: string[];
}

// Copywriting
export interface Copywriting {
  id: number;
  folder_id: number | null;
  title: string;
  content: string;
  scope: ResourceScope;
  owner_id: number | null;
  owner_name?: string;
  usage_count: number;
  last_used_at: string | null;
  tags: string[] | null;
  derived_from_id: number | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CopywritingCreate {
  title: string;
  content: string;
  folder_id?: number | null;
  scope: ResourceScope;
  tags?: string[];
}

export interface CopywritingUpdate {
  title?: string;
  content?: string;
  folder_id?: number | null;
  tags?: string[];
}

export interface CopywritingUse {
  edited_content?: string;
  save_as_new?: boolean;
  new_title?: string;
  new_folder_id?: number;
}

export interface CopywritingUseResponse {
  original: Copywriting;
  content_to_send: string;
  new_copywriting: Copywriting | null;
}

// Material
export interface Material {
  id: number;
  folder_id: number | null;
  name: string;
  file_type: MaterialType;
  file_size: number;
  file_path: string;
  file_url: string;
  content_type: string;
  thumbnail_url: string | null;
  scope: ResourceScope;
  owner_id: number | null;
  owner_name?: string;
  usage_count: number;
  last_used_at: string | null;
  tags: string[] | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface MaterialCreate {
  name: string;
  folder_id?: number | null;
  file_type: MaterialType;
  file_size: number;
  file_path: string;
  content_type: string;
  thumbnail_url?: string | null;
  scope: ResourceScope;
  tags?: string[];
}

export interface MaterialUpdate {
  name?: string;
  folder_id?: number | null;
  tags?: string[];
  thumbnail_url?: string | null;
}

// List responses
export interface CopywritingListResponse {
  total: number;
  items: Copywriting[];
}

export interface MaterialListResponse {
  total: number;
  items: Material[];
}

// Quick reply
export interface QuickReplyItem {
  type: 'copywriting' | 'material';
  data: Copywriting | Material;
  my_usage_count?: number;
  last_used_at?: string;
  recent_usage?: number;
}

// Query params
export interface ResourceQueryParams {
  folder_id?: number | null;
  scope?: 'all' | 'enterprise' | 'personal';
  owner_id?: number;
  keyword?: string;
  page?: number;
  page_size?: number;
}

export interface QuickReplyQueryParams {
  type?: 'all' | 'copywriting' | 'material';
  keyword?: string;
  scope?: 'all' | 'enterprise' | 'personal';
  folder_id?: number;
  sort?: 'popular' | 'recent' | 'created';
  limit?: number;
}

export interface TrendingQueryParams {
  type?: 'copywriting' | 'material';
  scope?: 'all' | 'enterprise' | 'personal';
  days?: 7 | 30 | 90;
  limit?: number;
}
